import React, {memo} from 'react';

import {Link} from 'react-router';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {useCurrentUser} from 'app/current-user/api';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {useCreateProduct} from 'app/market/api';
import {useDeleteTeam} from 'app/teams/api';
import {getTeamDetailPageURL} from 'app/urls';
import {gt, ngt} from 'app/utils/i18n';

import {StyledTeamListItem} from '../TeamItem';

import type {ActionsMenuItemsType} from 'app/general/widgets/ActionsButton';
import type {TeamSerializedType} from 'sweet-types';

interface Props {
  team: TeamSerializedType;
  isManager?: boolean;
  hideMembershipStatus?: boolean;
}

export const TeamListItem = memo<Props>(
  ({team, isManager, hideMembershipStatus}: Props) => {
    const [deleteTeam] = useDeleteTeam();
    const [createProduct] = useCreateProduct();
    const {data: currentUser} = useCurrentUser(undefined, {
      refetchOnMountOrArgChange: false,
    });

    const actions: ActionsMenuItemsType = [
      {
        text: gt('Delete Team'),
        key: 'cancel',
        icon: 'far fa-trash',
        callback: () => deleteTeam({id: team.id, name: team.name}).unwrap(),
      },
      {
        text: gt('Create Product'),
        key: 'product',
        icon: 'far fa-gift',
        callback: () =>
          createProduct({team: team.url, html_url: team.html_url}).unwrap(),
        disabled: currentUser?.is_super_manager !== true,
      },
    ];

    return (
      <StyledTeamListItem>
        <div className="col-12 col-lg text-truncate text-nowrap d-flex align-items-center">
          <Link
            to={getTeamDetailPageURL(team.id, team.slug)}
            className="text-dark font-weight-bold d-block d-sm-inline"
          >
            {/* show loading indicator while the user-team membership data is being fetched */}
            {hideMembershipStatus || isManager === false ? null : (
              <i
                className={`fa-fw fa mr-1 ${
                  isManager ? 'fa-star' : 'fa-circle-notch fa-spin'
                }`}
              />
            )}
            {team.name || <i>{gt('Untitled Team')}</i>}
          </Link>
          <span className="pl-2 text-muted font-weight-light font-italic">
            {team.description}
          </span>
        </div>

        <div className="col d-flex align-items-center pt-2 pt-lg-0">
          <div className="mr-4">
            {team.number_of_procedures != null ? (
              <LazyTooltip
                tooltip={ngt(
                  '%s procedure',
                  '%s procedures',
                  team.number_of_procedures
                )}
              >
                <span
                  className={`ml-1 badge badge-light ${
                    team.number_of_procedures === 0 ? 'empty' : ''
                  }`}
                >
                  <i className="fa fa-file" /> {team.number_of_procedures}
                </span>
              </LazyTooltip>
            ) : null}

            <LazyTooltip
              tooltip={ngt(
                '%s process',
                '%s processes',
                team.number_of_processes
              )}
            >
              <span
                className={`ml-1 badge badge-light ${
                  team.number_of_processes === 0 ? 'empty' : ''
                }`}
              >
                <i className="far fa-copy" /> {team.number_of_processes}
              </span>
            </LazyTooltip>

            <LazyTooltip
              tooltip={ngt(
                ' %s policy',
                '%s policies',
                team.number_of_policies
              )}
            >
              <span
                className={`ml-1 badge badge-light ${
                  team.number_of_policies === 0 ? 'empty' : ''
                }`}
              >
                <i className="fa fa-umbrella" /> {team.number_of_policies}
              </span>
            </LazyTooltip>
          </div>

          <span>
            {[
              team.num_managers !== 0
                ? ngt('%d Manager', '%d Managers', team.num_managers)
                : null,
              team.num_teammates + team.num_readers !== 0
                ? ngt(
                    '%d Member',
                    '%d Members',
                    team.num_teammates + team.num_readers
                  )
                : null,
            ]
              .filter((x) => x)
              .join(' / ')}
          </span>
        </div>
        <div className="col-1 col-sm-auto pl-0 d-flex align-items-start align-items-sm-center flex-row flex-column-reverse flex-sm-row">
          {team.can_edit ? (
            <ActionsButton actions={actions} />
          ) : (
            <span className="px-2 py-1">
              <i className="fa fa-fw" />
            </span>
          )}
        </div>
      </StyledTeamListItem>
    );
  }
);
